import React from 'react';
import autobind from 'autobind-decorator';
import {Button, Col, Row} from 'react-bootstrap';
import {
    BaseComponent
} from "../../BaseComponent";
import {
    Conditional
} from "../../Conditional";
import {
    ConfirmationDialog
} from "../../Common/ConfirmationDialog";
import {
    Glyphicon
} from "../../Common/GlyphIcon";
import {
    StreamBankErrorDispatchEnhance
} from "../../Common/Enhancements";

import {ObjectUtils} from "../../../Utils";

import {service as ConservationActionService} from "../../../Services/ConservationActionService";
import {service as SurveyAnswerService} from "../../../Services/SurveyAnswerService";
import {service as SurveyService} from "../../../Services/SurveyService";
import {service as SurveyTypeService} from "../../../Services/SurveyTypeService";


import {CopyAnswersModal} from "./CopyAnswersModal";

import {InfoIcon} from "../../Common/InfoIcon";
import {SurveyForm} from "../../Common/SurveyForm/SurveyForm";

@StreamBankErrorDispatchEnhance
export class SurveyComponent extends BaseComponent {
    constructor(props) {
        super(props);

        this.setInitialState({
            survey: null,
            surveyAnswers: [],
            surveyPlantQuantities: [],
            surveyRiskAssessments: [],
            showCopyAnswersModal: false,
            saveDialog: false,
            validation: {},
            editMode: false
        });
    }

    @autobind
    getConservationActionId(props) {
        if(!props) {
            props = this.props;
        }

        return props.match.params.conActId;
    }

    @autobind
    getConservationActionTypeId() {
        return this.props.ConservationActionTypeId;
    }

    @autobind
    getSurveyTypeId() {
        return this.props.SurveyTypeId;
    }

    @autobind
    getSurveyType() {
        let surveyType = null;
        if(this.state.surveyTypes) {
            surveyType = this.state.surveyTypes.find((x) => x.surveyTypeId == this.getSurveyTypeId());
        }
        return surveyType;
    }

    @autobind
    shouldReloadData(prevProps, prevState) {
        const conservationActionId = this.getConservationActionId();
        return conservationActionId && conservationActionId !== this.getConservationActionId(prevProps);
    }

    @autobind
    loadData() {
        const {OnConservationActionChange} = this.props;
        super.loadData();

        let surveyTypeId = this.getSurveyTypeId();
        let conservationActionId = this.getConservationActionId();

        this.props.MakePageRequest(
            () => Promise.all([
                SurveyService.GetSurveysByConservationActionId(surveyTypeId, conservationActionId),
                SurveyAnswerService.GetSurveyAnswers(surveyTypeId, conservationActionId),
                SurveyTypeService.Get(),
                ConservationActionService.Get(conservationActionId, false)
            ]),
            ([survey, surveyAnswers, surveyTypes, conservationAction]) => {
                if(!survey.result?.length) {
                    this.props.ChangeTool("summary");
                } else {
                    this.setState({
                        survey: survey.result?.[0],
                        surveyAnswers: surveyAnswers.result,
                        surveyPlantQuantities: surveyAnswers.SurveyAnswerPlantQuantityZone,
                        surveyRiskAssessments: surveyAnswers.risks,
                        surveyTypes: surveyTypes.result
                    });
                    OnConservationActionChange(conservationAction.result);
                }
            }
        );
    }

    @autobind
    copyAnswers(sourceConservationActionId, sourceSurveyTypeId) {

        this.setState({showCopyAnswersModal: false});

        let currentConservationActionId = this.getConservationActionId();
        let currentSurveyId = this.state.survey.surveyId;
        
        if (sourceConservationActionId && sourceSurveyTypeId && currentConservationActionId && currentSurveyId) {
            this.props.MakePageRequest(
                () => Promise.all([
                    SurveyAnswerService.CopySurveyAnswers(sourceConservationActionId, sourceSurveyTypeId, currentConservationActionId, currentSurveyId)
                ]),
                ([sourceAnswers]) => {
                    this.setState({
                        editMode: true,
                        surveyAnswers: sourceAnswers.result,
                        surveyRiskAssessments: sourceAnswers.risks,
                        surveyPlantQuantities: sourceAnswers.SurveyAnswerPlantQuantityZone,
                    });
                },
                (x) => {
                    this.props.ErrorDispatch.SetError({
                        Code: x.xhr.status,
                        Message: x.message,
                        Title: x.xhr.statusText,
                        StackTrace: x.data.stackTrace
                    });
                }
            );
        }
    }

    @autobind
    canPullPreviousAnswers() {
        // only allowed for non-permit survey types
        if(this.state.survey.surveyTypeId < 4) {
            return true;
        }
    }

    @autobind
    onValidation(questionId, message) {
        let validation = ObjectUtils.Clone(this.state.validation);

        if(message !== null) {
            validation[questionId] = message;
        } else {
            if(questionId in validation) {
                delete validation[questionId];
            }
        }

        this.setState({validation: validation});
    }

    @autobind
    onAnswerChange(answer) {
        let answers = ObjectUtils.Clone(this.state.surveyAnswers);
        let questionId = answer.surveyQuestionId;

        let existing = answers.find((x) => x.surveyQuestionId == questionId);
        if(existing) {
            let i = answers.indexOf(existing);

            if(i != -1) { //How would this be -1?
                //Answer: indexOf returns -1 instead of the more sane null if the element isn't in the array
                // https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Array/indexOf
                answers.splice(i, 1);
            }

            answers.push(answer);
        } else {
            answers.push(answer);
        }

        this.setState({surveyAnswers: answers});
    }

    @autobind
    deleteAnswer(answer) {
        let answers = ObjectUtils.Clone(this.state.surveyAnswers);
        answers = answers.filter((x) => x.surveyQuestionId != answer.surveyQuestionId);

        this.setState({surveyAnswers: answers});
    }

    @autobind
    onCustomDataChange(data) {
        this.setState({
            surveyPlantQuantities: data.PlantQuantities,
            surveyRiskAssessments: data.RiskAssessments
        });
    }

    @autobind
    save(displayPopup = true) {
        
        const conservationAction = this.props.ConservationAction;
        
        let answers = ObjectUtils.Clone(this.state.surveyAnswers);
        let quantities = ObjectUtils.Clone(this.state.surveyPlantQuantities);
        let risks = ObjectUtils.Clone(this.state.surveyRiskAssessments);

        // the server also filters by this, so this shouldn't
        // be necessary, but there is not reason to send this
        // extra data across the wire
        const surveyType = this.getSurveyType();
        let conservationActionSampleEventsToUpdate = null;
        let conservationActionPermitsToUpdate = null;
        if(surveyType?.conservationActionPhaseId && conservationAction?.ConservationActionSampleEvents) {
            let conservationActionSampleEvents = ObjectUtils.Clone(conservationAction.ConservationActionSampleEvents)
            conservationActionSampleEventsToUpdate = conservationActionSampleEvents
                .filter((casEvent) => casEvent.conservationActionPhaseId == surveyType.conservationActionPhaseId)
        }

        if(surveyType?.code === 'permit' && conservationAction?.ConservationActionPermits) {
            conservationActionPermitsToUpdate = conservationAction.ConservationActionPermits;
        }

        return this.props.MakePageRequest(
            () => SurveyAnswerService.UpdateSurveyAnswers(
                answers,
                quantities,
                risks,
                this.state.survey.surveyId,
                this.getConservationActionId(),
                conservationActionSampleEventsToUpdate,
                conservationActionPermitsToUpdate
            ),
            () => {
                this.setState({saveDialog: displayPopup}, () => {
                    this.loadData();
                });
            }
        );
    }

    @autobind
    enterEditMode() {
        this.setState({editMode: true});
    }

    @autobind
    exitEditMode(reloadData = true) {
        this.setState({editMode: false}, () => {
            if(reloadData) {
                this.loadData();
            }
        });
    }

    @autobind
    canEdit() {
        const conservationAction = this.props?.ConservationAction;
        if(conservationAction) {
            if(this.props.HasPermission('update_conservation_action')) {
                return this.props.EditContextNodeId(conservationAction.contextNodeId);
            }
        }
        return false;
    }

    @autobind
    isValid() {
        //if the validation object is empty we're good.
        return Object.keys(this.state.validation).length === 0;
    }

    render() {
        const {ConservationAction: conservationAction, OnConservationActionChange} = this.props;
        const conservationActionType = conservationAction?.ConservationActionType;
        let surveyType = null;

        if(this.state.surveyTypes) {
            surveyType = this.state.surveyTypes.find((x) => x.surveyTypeId == this.getSurveyTypeId());
        }

        // don't render anything until we are loaded.
        if(conservationActionType === null || surveyType === null) {
            return null;
        }

        return <Row>
            <ConfirmationDialog
                Title="Saved"
                Body="Save Successful!"
                Display={this.state.saveDialog}
                ConfirmationButtonMessage="OK"
                OnConfirm={() => {
                    this.setState({saveDialog: false});
                }}
            />
            <CopyAnswersModal
                show={this.state.showCopyAnswersModal}
                targetConservationActionId={conservationAction?.conservationActionId}
                targetConservationActionTypeId={this.getConservationActionTypeId()}
                targetSurveyTypeId={this.getSurveyTypeId()}
                onHide={ () => { this.setState({showCopyAnswersModal: false}); } }
                onConfirm={this.copyAnswers}
            />
            <Col xs={12} className="sb-print-show">
                <h2 className="atomic-sectionheader">
                    <span className="sb-survey-header sb-print-show">{conservationAction?.ContextNodeDetails?.name}{surveyType ? " - " + surveyType.name + " Survey" : ""}</span>
                </h2>
            </Col>
            <Col xs={12} className="sb-sticky-header">
                <h2 className="atomic-sectionheader">
                    <div className="float-right btn-edit">
                        <Conditional Condition={this.state.editMode}>
                            <Button
                                variant="save"
                                disabled={!this.isValid()}
                                onClick={() => this.save(false).then(() => { this.exitEditMode(false); })}
                            >
                                Save
                            </Button>
                            <Button
                                variant="cancel"
                                onClick={() => this.exitEditMode()}
                            >
                                Cancel
                            </Button>
                        </Conditional>
                        <Conditional Condition={!this.state.editMode && this.canEdit()}>
                            <Conditional Condition={this.canPullPreviousAnswers()}>
                                <Button
                                    variant="link"
                                    onClick={ x => {this.setState({showCopyAnswersModal: true});} }>
                                    <Glyphicon glyph="copy" />Copy Data From Existing Survey...
                                </Button>
                            </Conditional>
                            <Button
                                variant="link"
                                onClick={() => this.enterEditMode()}>
                                <Glyphicon glyph="pencil" />Edit
                            </Button>
                        </Conditional>
                    </div>
                    <span className="sb-survey-header">{conservationActionType?.name ?? ""}{surveyType ? ": " + surveyType.name : ""}</span>
                    <InfoIcon link={this.state.survey.infoLink}>{this.state.survey.infoTitle}</InfoIcon>
                </h2>
            </Col>
            <Col xs={12}>
                <SurveyForm
                    EditMode={this.state.editMode}
                    ConservationAction={conservationAction}
                    Survey={this.state.survey}
                    Answers={this.state.surveyAnswers}
                    Validation={this.state.validation}
                    IsValid={this.isValid()}
                    CustomData={{
                        PlantQuantities: this.state.surveyPlantQuantities,
                        RiskAssessments: this.state.surveyRiskAssessments
                    }}
                    OnAnswerChange={this.onAnswerChange}
                    OnValidation={this.onValidation}
                    DeleteAnswer={this.deleteAnswer}
                    OnConservationActionChange={OnConservationActionChange}
                    OnCustomDataChange={this.onCustomDataChange}
                    OnSave={this.save}
                    OnFinish={() => this.save(false).then(() => { this.exitEditMode(false); })}
                    {...this.props.SurveyFormProps}
                />
            </Col>
        </Row>;
    }
}


